import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, Pressable, FlatList, StyleSheet, ActivityIndicator, Alert } from 'react-native';
import { Dialog, YStack, XStack, Button } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import { useQuery } from '@tanstack/react-query';
import useGlobalUser from '@hooks/getGlobalUser';
import { useTranslation } from 'react-i18next';

export interface ProductSelectionModalHandle {
  openModal: () => void;
  closeModal: () => void;
}

interface ProductSelectionModalProps {
  onProductSelected: (product: any, paymentProvider: 'stripe' | 'paystack', paymentLink: string) => void;
  recipientId?: string;
  businessPhoneNumberId?: string;
}

const ProductSelectionModal = React.forwardRef<ProductSelectionModalHandle, ProductSelectionModalProps>(
  ({ onProductSelected, recipientId, businessPhoneNumberId }, ref) => {
    const [visible, setVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [selectedProvider, setSelectedProvider] = useState<'stripe' | 'paystack' | null>(null);
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const theme = themeSelector();
    const selectedId = useGlobalUser();
    const { t } = useTranslation();

    React.useImperativeHandle(ref, () => ({
      openModal: () => setVisible(true),
      closeModal: () => {
        setVisible(false);
        setSelectedProduct(null);
        setSelectedItem(null);
        setSelectedProvider(null);
      },
    }));

    // Fetch products (data folders with 'product' or 'products' tags)
    const { data: productsData, isLoading: isLoadingProducts } = useQuery({
      queryKey: ['products-for-payment', selectedId],
      queryFn: async () => {
        const data = await apis.getDataFolders({ userId: selectedId, dataFilter: 'products' });
        return Array.isArray(data) ? data : (data?.data || []);
      },
      enabled: visible && !!selectedId,
      staleTime: 5 * 60 * 1000,
    });

    // Fetch data items for selected product
    const { data: productItems, isLoading: isLoadingItems } = useQuery({
      queryKey: ['product-items', selectedProduct?._id],
      queryFn: async () => {
        if (!selectedProduct?._id) return [];
        const response = await apis.getDataContent(selectedProduct._id, 'all');
        return response?.data?.dataContent || [];
      },
      enabled: visible && !!selectedProduct?._id,
      staleTime: 5 * 60 * 1000,
    });

    // Reset selected item when product changes
    React.useEffect(() => {
      if (selectedProduct) {
        setSelectedItem(null);
        setSelectedProvider(null);
      }
    }, [selectedProduct]);

    const products = useMemo(() => {
      if (!productsData) return [];
      return productsData.filter((item: any) => {
        const tags = item.dataTags || [];
        return tags.some((tag: string) => 
          ['product', 'products', 'Product', 'Products'].includes(tag)
        );
      });
    }, [productsData]);

    const handleProductSelect = useCallback((product: any) => {
      setSelectedProduct(product);
      setSelectedItem(null);
      setSelectedProvider(null);
    }, []);

    const handleItemSelect = useCallback((item: any) => {
      setSelectedItem(item);
      setSelectedProvider(null);
    }, []);

    const handleProviderSelect = useCallback((provider: 'stripe' | 'paystack') => {
      setSelectedProvider(provider);
    }, []);

    const handleGeneratePaymentLink = useCallback(async () => {
      if (!selectedProduct || !selectedItem || !selectedProvider) {
        Alert.alert('Error', 'Please select a product, item, and payment provider');
        return;
      }

      setIsGeneratingLink(true);
      try {
        // Generate payment link via backend
        const response = await apis.generateProductPaymentLink({
          dataFolderId: selectedProduct._id,
          dataItemId: selectedItem._id,
          paymentProvider: selectedProvider,
          recipientId,
          businessPhoneNumberId,
        });

        if (response?.paymentLink) {
          onProductSelected(selectedItem, selectedProvider, response.paymentLink);
          setVisible(false);
          setSelectedProduct(null);
          setSelectedItem(null);
          setSelectedProvider(null);
        } else {
          Alert.alert('Error', response?.error || 'Failed to generate payment link');
        }
      } catch (error: any) {
        console.error('Error generating payment link:', error);
        Alert.alert('Error', error?.message || 'Failed to generate payment link');
      } finally {
        setIsGeneratingLink(false);
      }
    }, [selectedProduct, selectedItem, selectedProvider, recipientId, businessPhoneNumberId, onProductSelected]);

    const getProductTitle = (product: any) => {
      return product.dataTitle || product.title || 'Untitled Product';
    };

    const getProductDescription = (product: any) => {
      return product.description || product.dataDescription || '';
    };

    const getItemPrice = (item: any) => {
      const data = item.data || {};
      const price = data.price || data.cost || data.amount;
      if (price) {
        const currency = data.currency || 'USD';
        if (typeof price === 'number') {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
          }).format(price);
        }
        return price;
      }
      return null;
    };

    const getItemTitle = (item: any) => {
      const data = item.data || {};
      return data.title || data.name || data.product_name || 'Untitled Item';
    };

    const getItemDescription = (item: any) => {
      const data = item.data || {};
      return data.description || data.product_description || '';
    };

    return (
      <Dialog modal open={visible} onOpenChange={setVisible}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.5}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0 }}
            exitStyle={{ x: 0, y: 10, opacity: 0 }}
            maxWidth={600}
            width="90%"
            maxHeight="80%"
            backgroundColor={theme ? 'white' : '#1e293b'}
          >
            <Dialog.Title style={{ color: theme ? '#000' : '#fff', fontSize: 20, fontWeight: '700' }}>
              Select Product & Payment Method
            </Dialog.Title>
            <Dialog.Description style={{ color: theme ? '#666' : '#999', marginBottom: 16 }}>
              Choose a product and payment provider to send a payment link
            </Dialog.Description>

            <YStack gap="$3" paddingVertical="$2" maxHeight="70%">
              {/* Product Selection */}
              <YStack gap="$2">
                <Text style={{ color: theme ? '#000' : '#fff', fontSize: 16, fontWeight: '600' }}>
                  Select Product
                </Text>
                {isLoadingProducts ? (
                  <View style={{ padding: 20, alignItems: 'center' }}>
                    <ActivityIndicator size="small" color={theme ? '#000' : '#fff'} />
                  </View>
                ) : products.length === 0 ? (
                  <View style={{ padding: 20, alignItems: 'center' }}>
                    <MaterialIcons name="inventory-2" size={48} color={theme ? '#ccc' : '#666'} />
                    <Text style={{ color: theme ? '#666' : '#999', marginTop: 8, textAlign: 'center' }}>
                      No products found. Create a product collection with 'product' or 'products' tag.
                    </Text>
                  </View>
                ) : (
                  <FlatList
                    data={products}
                    keyExtractor={(item) => item._id}
                    style={{ maxHeight: 200 }}
                    renderItem={({ item }) => {
                      const isSelected = selectedProduct?._id === item._id;
                      return (
                        <Pressable
                          onPress={() => handleProductSelect(item)}
                          style={[
                            styles.productItem,
                            {
                              backgroundColor: isSelected
                                ? (theme ? '#e3f2fd' : '#1e3a5f')
                                : (theme ? '#f8fafc' : '#334155'),
                              borderColor: isSelected
                                ? (theme ? '#2196f3' : '#4a90e2')
                                : (theme ? '#e2e8f0' : '#475569'),
                            },
                          ]}
                        >
                          <View style={{ flex: 1 }}>
                            <Text
                              style={{
                                color: theme ? '#000' : '#fff',
                                fontSize: 16,
                                fontWeight: '600',
                                marginBottom: 4,
                              }}
                            >
                              {getProductTitle(item)}
                            </Text>
                            {getProductDescription(item) && (
                              <Text
                                style={{
                                  color: theme ? '#666' : '#999',
                                  fontSize: 12,
                                }}
                                numberOfLines={2}
                              >
                                {getProductDescription(item)}
                              </Text>
                            )}
                          </View>
                          {isSelected && (
                            <MaterialIcons
                              name="check-circle"
                              size={24}
                              color={theme ? '#2196f3' : '#4a90e2'}
                            />
                          )}
                        </Pressable>
                      );
                    }}
                  />
                )}
              </YStack>

              {/* Item Selection */}
              {selectedProduct && productItems && productItems.length > 0 && (
                <YStack gap="$2">
                  <Text style={{ color: theme ? '#000' : '#fff', fontSize: 16, fontWeight: '600' }}>
                    Select Product Item
                  </Text>
                  {isLoadingItems ? (
                    <View style={{ padding: 20, alignItems: 'center' }}>
                      <ActivityIndicator size="small" color={theme ? '#000' : '#fff'} />
                    </View>
                  ) : (
                    <FlatList
                      data={productItems}
                      keyExtractor={(item) => item._id}
                      style={{ maxHeight: 200 }}
                      renderItem={({ item }) => {
                        const isSelected = selectedItem?._id === item._id;
                        return (
                          <Pressable
                            onPress={() => handleItemSelect(item)}
                            style={[
                              styles.productItem,
                              {
                                backgroundColor: isSelected
                                  ? (theme ? '#e3f2fd' : '#1e3a5f')
                                  : (theme ? '#f8fafc' : '#334155'),
                                borderColor: isSelected
                                  ? (theme ? '#2196f3' : '#4a90e2')
                                  : (theme ? '#e2e8f0' : '#475569'),
                              },
                            ]}
                          >
                            <View style={{ flex: 1 }}>
                              <Text
                                style={{
                                  color: theme ? '#000' : '#fff',
                                  fontSize: 16,
                                  fontWeight: '600',
                                  marginBottom: 4,
                                }}
                              >
                                {getItemTitle(item)}
                              </Text>
                              {getItemDescription(item) && (
                                <Text
                                  style={{
                                    color: theme ? '#666' : '#999',
                                    fontSize: 12,
                                  }}
                                  numberOfLines={2}
                                >
                                  {getItemDescription(item)}
                                </Text>
                              )}
                              {getItemPrice(item) && (
                                <Text
                                  style={{
                                    color: theme ? '#2196f3' : '#4a90e2',
                                    fontSize: 14,
                                    fontWeight: '600',
                                    marginTop: 4,
                                  }}
                                >
                                  {getItemPrice(item)}
                                </Text>
                              )}
                            </View>
                            {isSelected && (
                              <MaterialIcons
                                name="check-circle"
                                size={24}
                                color={theme ? '#2196f3' : '#4a90e2'}
                              />
                            )}
                          </Pressable>
                        );
                      }}
                    />
                  )}
                </YStack>
              )}

              {/* Payment Provider Selection */}
              {selectedProduct && selectedItem && (
                <YStack gap="$2">
                  <Text style={{ color: theme ? '#000' : '#fff', fontSize: 16, fontWeight: '600' }}>
                    Select Payment Provider
                  </Text>
                  <XStack gap="$2" flexWrap="wrap">
                    <Pressable
                      onPress={() => handleProviderSelect('stripe')}
                      style={[
                        styles.providerButton,
                        {
                          backgroundColor:
                            selectedProvider === 'stripe'
                              ? (theme ? '#635bff' : '#4a5568')
                              : (theme ? '#f1f5f9' : '#2d3748'),
                          borderColor:
                            selectedProvider === 'stripe'
                              ? (theme ? '#635bff' : '#4a90e2')
                              : (theme ? '#e2e8f0' : '#475569'),
                        },
                      ]}
                    >
                      <MaterialCommunityIcons
                        name="credit-card"
                        size={20}
                        color={selectedProvider === 'stripe' ? '#fff' : (theme ? '#666' : '#999')}
                      />
                      <Text
                        style={{
                          color: selectedProvider === 'stripe' ? '#fff' : (theme ? '#666' : '#999'),
                          fontSize: 14,
                          fontWeight: '600',
                          marginLeft: 8,
                        }}
                      >
                        Stripe
                      </Text>
                    </Pressable>
                    <Pressable
                      onPress={() => handleProviderSelect('paystack')}
                      style={[
                        styles.providerButton,
                        {
                          backgroundColor:
                            selectedProvider === 'paystack'
                              ? (theme ? '#00b876' : '#2d5a3d')
                              : (theme ? '#f1f5f9' : '#2d3748'),
                          borderColor:
                            selectedProvider === 'paystack'
                              ? (theme ? '#00b876' : '#4a90e2')
                              : (theme ? '#e2e8f0' : '#475569'),
                        },
                      ]}
                    >
                      <MaterialCommunityIcons
                        name="wallet"
                        size={20}
                        color={selectedProvider === 'paystack' ? '#fff' : (theme ? '#666' : '#999')}
                      />
                      <Text
                        style={{
                          color: selectedProvider === 'paystack' ? '#fff' : (theme ? '#666' : '#999'),
                          fontSize: 14,
                          fontWeight: '600',
                          marginLeft: 8,
                        }}
                      >
                        Paystack
                      </Text>
                    </Pressable>
                  </XStack>
                </YStack>
              )}
            </YStack>

            <XStack gap="$2" marginTop="$4" justifyContent="flex-end">
              <Dialog.Close displayWhenAdapted asChild>
                <Button
                  backgroundColor={theme ? '#f0f0f0' : '#475569'}
                  color={theme ? '#000' : '#fff'}
                  onPress={() => {
                    setVisible(false);
                    setSelectedProduct(null);
                    setSelectedItem(null);
                    setSelectedProvider(null);
                  }}
                >
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                backgroundColor={selectedProduct && selectedItem && selectedProvider ? '#25D366' : '#ccc'}
                color="white"
                disabled={!selectedProduct || !selectedItem || !selectedProvider || isGeneratingLink}
                onPress={handleGeneratePaymentLink}
              >
                {isGeneratingLink ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  'Generate & Send Link'
                )}
              </Button>
            </XStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    );
  }
);

ProductSelectionModal.displayName = 'ProductSelectionModal';

const styles = StyleSheet.create({
  productItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    marginBottom: 8,
  },
  providerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 2,
    flex: 1,
    minWidth: '45%',
    justifyContent: 'center',
  },
});

export default ProductSelectionModal;

